import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import {
  AllTableColumn,
  AllTableSorting,
} from '@prlw/core/monitor/types/all-table';
import { filter, first, map, shareReplay, switchMap } from 'rxjs/operators';
import {
  allTableCompareData,
  getAllTableSortDefault,
} from '@prlw/core/monitor/utils/all-table';
import { UserSettingsController } from '@prlw/core/user-settings/user-settings.controller';
import { Setting } from '@prlw/core/user-settings/setting.interface';
import { AllInstruments } from '@prlw/core/monitor/all-instruments.entity';
import { AllTableInstrumentsService } from '@prlw/core/monitor/all-table-instruments.service';
import { allTableColumns } from '@prlw/core/monitor/init/all-table';
import { IObservableWithPending } from '@prlw/infrastructure/observableWithPending';

const ALL_TABLE_BASIS_COLUMN_NAME = 'Завод';
const ALL_TABLE_SORTING_SETTING = new Setting<AllTableSorting>(
  'all-table-sorting',
);
const ALL_TABLE_COLUMNS = new Setting<AllTableColumn[]>('all-table-column');

@Injectable({
  providedIn: 'root',
})
export class AllTableService {
  public readonly allTableSorting$: Observable<AllTableSorting>;
  public readonly allTableColumns$: Observable<AllTableColumn[]>;
  public readonly sortedAllInstruments$: Observable<AllInstruments[]>;

  constructor(
    monitorProvider: AllTableInstrumentsService,
    private readonly userSettingsController: UserSettingsController,
  ) {
    this.allTableSorting$ = userSettingsController
      .setting$(
        ALL_TABLE_SORTING_SETTING,
        getAllTableSortDefault(ALL_TABLE_BASIS_COLUMN_NAME),
      )
      .pipe(shareReplay(1));

    this.allTableColumns$ = userSettingsController
      .setting$(ALL_TABLE_COLUMNS, allTableColumns)
      .pipe(shareReplay(1));

    this.sortedAllInstruments$ = combineLatest([
      monitorProvider.allInstruments$.pipe(
        filter(
          (
            instrumentsWithPending,
          ): instrumentsWithPending is NonNullable<
            IObservableWithPending<AllInstruments[]>
          > => Boolean(instrumentsWithPending),
        ),
        map((instrumentsWithPending) => instrumentsWithPending.result),
        filter((instruments): instruments is AllInstruments[] => !!instruments),
      ),
      this.allTableSorting$,
    ]).pipe(
      map(([instruments, sorting]) =>
        instruments.sort((a, b) => allTableCompareData(a, b, sorting)),
      ),
    );
  }

  public allTableSortColumn(columnName: string): void {
    this.allTableSorting$
      .pipe(
        first(),
        switchMap((allTableSorting) => {
          const { columnName: oldColumn, direction: oldDirection } =
            allTableSorting;
          const defaultSort = getAllTableSortDefault(columnName);
          if (defaultSort.columnName === oldColumn) {
            defaultSort.direction = oldDirection === 'ASC' ? 'DES' : 'ASC';
          }
          return this.userSettingsController.updateSetting(
            ALL_TABLE_SORTING_SETTING,
            defaultSort,
          );
        }),
      )
      .subscribe();
  }

  public updateColumn(columns: AllTableColumn[]): void {
    this.userSettingsController
      .updateSetting(ALL_TABLE_COLUMNS, columns)
      .pipe(first())
      .subscribe();
  }
}
